import { useState } from 'react';
import { V2Badge, V2Button, V2Card, V2CardHeader, Icon } from '@sakhtar/design-system/react';
import { BarChart } from '../../../lib/charts/index.js';
import { toEnglishDigits } from '../../../lib/phoneNumber.js';
import { useAiPreview } from '../aiPreview.js';
import { dayLabel, formatTokenDelta, formatTokens } from './format.js';
import { percentOf } from './pricing.js';
import {
  SAMPLE_CURRENT_PURCHASE_DATE,
  SAMPLE_DAILY_USAGE,
  type WalletHistoryRow,
} from './sample.js';
import { Link } from 'react-router-dom';
import { useMobileShell } from '../useMobileShell.js';

const MAX_THRESHOLD_DIGITS = 9;

const CHART_DATA = SAMPLE_DAILY_USAGE.map((value, index) => ({
  label: dayLabel(index + 1),
  value,
}));

type BalanceTier = 'ok' | 'low' | 'empty';

function balanceTier(balance: number, threshold: number): BalanceTier {
  if (balance <= 0) return 'empty';
  if (balance <= threshold) return 'low';
  return 'ok';
}

export interface WalletOverviewProps {
  readonly threshold: number;
  readonly onThresholdChange: (value: number) => void;
  /** Purchase is allowed (real permission AND no blocking forced state). */
  readonly canBuy: boolean;
  /** Why purchase is unavailable, for the explanatory alert. */
  readonly blockedReason: 'permission' | 'suspended' | null;
  readonly onBuy: () => void;
  readonly onShowHistory: () => void;
  readonly recentRows: readonly WalletHistoryRow[];
}

export function WalletOverview({
  threshold,
  onThresholdChange,
  canBuy,
  blockedReason,
  onBuy,
  onShowHistory,
  recentRows,
}: WalletOverviewProps) {
  const ai = useAiPreview();
  const tier = balanceTier(ai.tokens.balance, threshold);
  const percent = percentOf(ai.tokens.balance, ai.tokens.purchased);
  // The chart is the only thing whose size is data-driven: shorter on phones
  // (design M5 draws it 120px tall at full width).
  const mobile = useMobileShell();

  return (
    <div className="ai-wallet-overview">
      <div className="ai-wallet-head">
        <p className="ai-wallet-head__intro">
          خرید توکن داخل همین ماژول انجام می‌شود. پلن و اشتراک جداگانه وجود ندارد؛ فقط بستهٔ توکن
          خریداری می‌شود و مصرف از موجودی کسر می‌شود.
        </p>
        <div className="ai-wallet-actions" aria-label="دسترسی سریع">
          {canBuy ? (
            <V2Button
              variant="primary"
              icon="cart"
              className="ai-wallet-buy--inline"
              onClick={onBuy}
            >
              خرید توکن
            </V2Button>
          ) : null}
          <V2Button variant="secondary" icon="invoice" onClick={onShowHistory}>
            تاریخچهٔ کیف پول
          </V2Button>
          {ai.isTenantAdmin ? (
            <Link to={`${ai.base}/ai/settings`} className="sk-btn sk-btn--ghost">
              تنظیمات دستیار
            </Link>
          ) : null}
        </div>
      </div>
      {blockedReason === 'permission' ? (
        <div className="sk-alert sk-alert--warn" role="status">
          <Icon name="shield" size="md" />
          <div className="sk-alert__body">
            <div className="sk-alert__title">خرید توکن فقط برای مدیر تننت امکان‌پذیر است</div>
            <div className="ai-wallet-note">
              {tier === 'empty'
                ? 'موجودی توکن تمام شده است. برای شارژ به مدیر سیستم اطلاع دهید.'
                : 'موجودی و تاریخچه برای شما قابل مشاهده است؛ برای شارژ موجودی به مدیر سیستم اطلاع دهید.'}
            </div>
          </div>
        </div>
      ) : null}
      {blockedReason === 'suspended' ? (
        <div className="sk-alert sk-alert--warn" role="status">
          <Icon name="shield" size="md" />
          <div className="sk-alert__body">
            <div className="sk-alert__title">تننت معلق است</div>
            <div className="ai-wallet-note">
              تا رفع تعلیق، خرید توکن ممکن نیست. موجودی و تاریخچه همچنان خواندنی می‌ماند.
            </div>
          </div>
        </div>
      ) : null}
      {tier === 'empty' && blockedReason === null ? (
        <div className="sk-alert sk-alert--warn" role="status">
          <Icon name="shield" size="md" />
          <div className="sk-alert__body">
            <div className="sk-alert__title">موجودی توکن تمام شده است</div>
            <div className="ai-wallet-note">
              سابقهٔ گفتگوها خواندنی می‌ماند و چیزی حذف نمی‌شود. برای ادامه، بسته‌ای انتخاب کنید.
            </div>
          </div>
          <V2Button
            variant="primary"
            size="sm"
            icon="cart"
            className="ai-wallet-buy--inline"
            onClick={onBuy}
          >
            خرید توکن
          </V2Button>
        </div>
      ) : null}

      <div className="ai-wallet-top">
        <section className="ai-wallet-balance" data-tier={tier} aria-label="موجودی فعلی">
          <div className="ai-wallet-balance__label">
            <span>موجودی فعلی</span>
            <span className="ai-wallet-sample ai-wallet-sample--on-brand">نمونه</span>
            {tier === 'low' ? (
              <span className="ai-wallet-balance__tier">کمتر از آستانهٔ هشدار</span>
            ) : null}
            {tier === 'empty' ? <span className="ai-wallet-balance__tier">موجودی صفر</span> : null}
          </div>
          <div className="ai-wallet-balance__value">
            <span className="sk-num ai-wallet-balance__num">{formatTokens(ai.tokens.balance)}</span>
            <span className="ai-wallet-balance__unit">توکن</span>
          </div>
          <div
            className="ai-wallet-balance__bar"
            role="progressbar"
            aria-valuemin={0}
            aria-valuemax={100}
            aria-valuenow={percent}
            aria-label="سهم موجودی از توکن‌های خریداری‌شده"
          >
            <div
              className="ai-wallet-balance__fill"
              style={{ ['--ai-wallet-pct' as string]: `${percent}%` }}
            />
          </div>
          <div className="ai-wallet-balance__foot">
            از <span className="sk-num">{formatTokens(ai.tokens.purchased)}</span> توکن بستهٔ جاری ·
            خرید <span className="sk-num">{SAMPLE_CURRENT_PURCHASE_DATE}</span>
          </div>
        </section>

        <ThresholdCard
          threshold={threshold}
          editable={ai.isTenantAdmin}
          onChange={onThresholdChange}
        />
      </div>

      <div className="ai-wallet-mid">
        <V2Card>
          <V2CardHeader
            icon="trend"
            title="مصرف ۳۰ روز اخیر"
            subtitle="توکن در روز"
            actions={<V2Badge tone="neutral">نمونه</V2Badge>}
          />
          <div className="ai-wallet-chart">
            <BarChart
              data={CHART_DATA}
              height={mobile ? 168 : 260}
              ariaLabel="نمودار مصرف روزانهٔ توکن (نمونه)"
            />
          </div>
        </V2Card>

        <V2Card>
          <V2CardHeader
            icon="clock"
            title="آخرین تراکنش‌ها"
            actions={<V2Badge tone="neutral">نمونه</V2Badge>}
          />
          <ul className="ai-wallet-recent">
            {recentRows.map((row) => (
              <li key={row.id} className="ai-wallet-recent__item">
                <div className="ai-wallet-recent__main">
                  <span className="ai-wallet-recent__ref">{row.reference}</span>
                  <span className="sk-num ai-wallet-recent__date">{row.date}</span>
                </div>
                <span className="sk-num ai-wallet-delta" data-sign={Math.sign(row.delta)}>
                  {formatTokenDelta(row.delta)}
                </span>
              </li>
            ))}
          </ul>
          <div className="ai-wallet-card-foot">
            <V2Button variant="ghost" size="sm" onClick={onShowHistory}>
              مشاهدهٔ همهٔ تراکنش‌ها
            </V2Button>
          </div>
        </V2Card>
      </div>

      <RulesCard />

      {/* ≤ 899px only (hidden by CSS above): the design's M5 pins «خرید توکن»
          above the bottom nav; the inline buttons are hidden at that width. */}
      {canBuy ? (
        <div className="ai-wallet-pinned">
          <V2Button variant="primary" size="lg" icon="cart" onClick={onBuy}>
            خرید توکن
          </V2Button>
        </div>
      ) : null}
    </div>
  );
}

function ThresholdCard({
  threshold,
  editable,
  onChange,
}: {
  threshold: number;
  editable: boolean;
  onChange: (value: number) => void;
}) {
  const [draft, setDraft] = useState(String(threshold));
  const draftNumber = draft === '' ? 0 : Number(draft);
  const dirty = draftNumber !== threshold;

  return (
    <section className="ai-wallet-threshold" aria-label="آستانهٔ هشدار موجودی">
      <span className="ai-wallet-threshold__title">آستانهٔ هشدار موجودی</span>
      <span className="sk-input-group">
        <input
          className="ltr sk-num"
          inputMode="numeric"
          aria-label="آستانهٔ هشدار موجودی (توکن)"
          readOnly={!editable}
          value={draftNumber.toLocaleString('fa-IR')}
          onChange={(event) =>
            setDraft(
              toEnglishDigits(event.target.value).replace(/\D/g, '').slice(0, MAX_THRESHOLD_DIGITS),
            )
          }
        />
        <span className="ai-wallet-threshold__unit">توکن</span>
      </span>
      <span className="ai-wallet-note">
        زیر این مقدار، کارت موجودی کهربایی می‌شود. اعلان به مدیران هنوز وصل نیست.
      </span>
      {editable ? (
        <>
          <V2Button
            variant="secondary"
            size="sm"
            disabled={!dirty}
            onClick={() => onChange(draftNumber)}
          >
            اعمال در پیش‌نمایش
          </V2Button>
          <span className="ai-wallet-note ai-wallet-note--subtle">
            ذخیره نمی‌شود؛ فقط تا بستن این صفحه اعمال می‌شود.
          </span>
        </>
      ) : null}
    </section>
  );
}

const RULES: readonly { label: string; text: string }[] = [
  {
    label: 'هزینه',
    text: 'از کیف پول توکن تننت کسر می‌شود. پلن و اشتراک جداگانه وجود ندارد؛ فقط بستهٔ توکن خریداری می‌شود.',
  },
  {
    label: 'برآورد و مصرف',
    text: 'پیش از ارسال پرسش «برآورد» نمایش داده می‌شود و در پایان پاسخ «مصرف واقعی». تنها عدد تقریبی همین برآورد است.',
  },
  {
    label: 'محرمانگی',
    text: 'سابقهٔ هر کاربر خصوصی است. مدیر تننت متن گفتگوها را نمی‌بیند، فقط مصرف را.',
  },
];

function RulesCard() {
  return (
    <V2Card>
      <V2CardHeader icon="info" title="نحوهٔ محاسبهٔ مصرف" />
      <dl className="ai-wallet-rules">
        {RULES.map((rule) => (
          <div key={rule.label} className="ai-wallet-rules__row">
            <dt>{rule.label}</dt>
            <dd>{rule.text}</dd>
          </div>
        ))}
      </dl>
    </V2Card>
  );
}
